import { call } from '@rental/contracts/client'
import { format, formatDate, plural } from '@rental/i18n'
import { Button } from '@rental/ui'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { FileSearch, Plus, SearchX } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { DataTable } from '../components/DataTable'
import { EmptyState } from '../components/EmptyState'
import { PageHeader } from '../components/PageHeader'
import { ResultCount } from '../components/Results'
import { SearchField, SearchForm, useSearchFields } from '../components/SearchForm'
import { StatusChip } from '../components/StatusChip'
import { Guard, LoadState } from '../components/states'
import { TabLinks, tabClass } from '../components/TabLinks'
import { FineForm } from '../fines/FineForm'
import { deadlineTone, deadlineWords, FINES_QUERY_KEY, statusTone } from '../fines/fines'
import { fineChanged } from '../fines/queries'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { BookingCode } from '../reports/BookingCode'

const route = getRouteApi('/fines')

/**
 * S6 (CMP-2, decision 4): the fines register, for the holders of
 * fines.manage (the Owner). The open ones by deadline, or all of them; found
 * by plate or notice number. A notice is registered here, then followed on
 * its own page. UI-2e: the views are links, the search follows the address,
 * and only the count is said as the list changes, never the table.
 */
export function FinesScreen() {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'fines.manage')
  const queryClient = useQueryClient()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const view = search.view ?? 'open'
  const asked = { q: search.q ?? '' }
  // Back, or a link to another search: the field follows the address.
  const [form, setForm] = useSearchFields(asked)
  const [registering, setRegistering] = useState(false)
  const list = useQuery({
    queryKey: [...FINES_QUERY_KEY, 'list', view, search.q ?? null],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/fines', {
            params: { query: { view, ...(search.q ? { q: search.q } : {}) } },
          }),
        ),
      ),
    enabled: allowed,
    placeholderData: keepPreviousData,
  })
  const data = list.data
  const copy = m.fines
  const countId = useId()
  const registerBox = useRef<HTMLSpanElement>(null)
  const formBox = useRef<HTMLDivElement>(null)
  const moved = useRef<'opened' | 'cancelled' | null>(null)

  // The form takes the focus as it opens; Annulla gives it back to the button that opened it.
  useEffect(() => {
    if (moved.current === 'opened' && registering) formBox.current?.querySelector('input')?.focus()
    if (moved.current === 'cancelled' && !registering) {
      registerBox.current?.querySelector('button')?.focus()
    }
    moved.current = null
  }, [registering])

  return (
    <Guard allowed={allowed} title={copy.title}>
      <PageHeader
        title={copy.title}
        intro={copy.intro}
        actions={
          registering ? null : (
            <span ref={registerBox}>
              <Button
                variant="staff"
                onClick={() => {
                  moved.current = 'opened'
                  setRegistering(true)
                }}
              >
                <Plus aria-hidden="true" className="size-4" />
                {copy.register}
              </Button>
            </span>
          )
        }
      >
        <div className="flex flex-col gap-4 pt-2">
          <TabLinks label={copy.title}>
            {(['open', 'all'] as const).map((key) => (
              <Link
                key={key}
                to="/fines"
                search={{ ...search, view: key === 'all' ? 'all' : undefined }}
                // The router marks a link current itself, and by default a part of the address
                // will do: "Da gestire" (no view) was current on "Tutte" too. Exactly, as `view`.
                activeOptions={{ exact: true }}
                aria-current={view === key ? 'page' : undefined}
                className={tabClass(view === key)}
              >
                {copy.views[key]}
              </Link>
            ))}
          </TabLinks>
          <SearchForm
            label={copy.title}
            filtered={search.q !== undefined}
            onSubmit={() =>
              void navigate({ to: '/fines', search: { ...search, q: form.q.trim() || undefined } })
            }
            onClear={() => void navigate({ to: '/fines', search: { ...search, q: undefined } })}
          >
            <SearchField
              label={copy.search}
              hint={copy.searchHint}
              value={form.q}
              onChange={(q) => setForm({ q })}
            />
          </SearchForm>
          <ResultCount
            id={countId}
            count={data ? plural(locale, copy.found, data.total) : null}
            range={
              data && data.total > data.shown
                ? format(copy.shown, { shown: data.shown, total: data.total })
                : null
            }
            asked={JSON.stringify({ view, q: search.q ?? null })}
            fetching={list.isPlaceholderData}
          />
        </div>
      </PageHeader>
      {registering ? (
        <div ref={formBox}>
          <FineForm
            surface="pane"
            onDone={async (fine) => {
              await fineChanged(queryClient, fine)
              void navigate({
                to: '/fines/$fineId',
                params: { fineId: fine.id },
                search: { done: 'registered' },
              })
            }}
            onCancel={() => {
              moved.current = 'cancelled'
              setRegistering(false)
            }}
          />
        </div>
      ) : null}
      <LoadState data={data} problem={problemOf(list.error)}>
        {(data) =>
          data.items.length === 0 ? (
            search.q === undefined ? (
              // Nothing to handle is not nothing at all (UI-2g): the closed ones are in Tutte.
              view === 'open' ? (
                <EmptyState icon={FileSearch} title={copy.noneOpen}>
                  {copy.fillsOpen}
                </EmptyState>
              ) : (
                <EmptyState icon={FileSearch} title={copy.none}>
                  {copy.fills}
                </EmptyState>
              )
            ) : (
              <EmptyState icon={SearchX} title={copy.none}>
                {m.lists.change}
              </EmptyState>
            )
          ) : (
            <div
              className="glass-pane flex flex-col rounded-card p-4 sm:p-6"
              aria-busy={list.isPlaceholderData || undefined}
            >
              <DataTable
                label={copy.views[view]}
                testId="fines-table"
                rows={data.items}
                rowKey={(fine) => fine.id}
                columns={[
                  {
                    header: copy.columns.notice,
                    rowHeader: true,
                    cell: (fine) => (
                      <>
                        <Link
                          to="/fines/$fineId"
                          params={{ fineId: fine.id }}
                          className="font-bold touch-target"
                        >
                          {fine.noticeNumber}
                        </Link>
                        <span className="block font-regular text-text-muted">{fine.authority}</span>
                        {/* The plate under its notice: the search finds a fine by either. */}
                        <span className="figures block font-regular">
                          <span className="sr-only">{copy.columns.plate}: </span>
                          {fine.plate}
                        </span>
                      </>
                    ),
                  },
                  {
                    header: copy.columns.violated,
                    cell: (fine) => (
                      <>
                        <span className="block whitespace-nowrap">
                          {formatDate(locale, fine.violatedOn, 'short')}
                        </span>
                        <span className="figures block text-text-muted">{fine.violatedAt}</span>
                      </>
                    ),
                  },
                  {
                    header: copy.columns.booking,
                    cell: (fine) => (
                      <>
                        {fine.code ? (
                          <BookingCode code={fine.code} />
                        ) : (
                          <span className="text-text-muted">{m.morningEmail.fineNoBooking}</span>
                        )}
                        {/* The fee is charged on the booking: said under it, not in a column of dashes. */}
                        {fine.feeCharged ? (
                          <span className="block text-text-muted">{copy.feeCharged}</span>
                        ) : null}
                      </>
                    ),
                  },
                  {
                    header: copy.columns.status,
                    cell: (fine) => (
                      <StatusChip tone={statusTone(fine.status)}>
                        {copy.status[fine.status]}
                      </StatusChip>
                    ),
                  },
                  {
                    header: copy.columns.deadline,
                    cell: (fine) => (
                      <span className="flex flex-col items-start gap-1">
                        <span className="whitespace-nowrap">
                          {formatDate(locale, fine.deadline.on, 'short')}
                        </span>
                        <StatusChip tone={deadlineTone(fine.deadline.state)}>
                          {deadlineWords(fine.deadline, locale, m)}
                        </StatusChip>
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          )
        }
      </LoadState>
    </Guard>
  )
}
